import { DfFacility } from '../../../core/df/df.types';
import { KsApplication, hasConfirmedAmount } from '../../../core/kickstart/kickstart.types';
import {
  PreApplication,
  PreApplicationReviewStatus,
} from '../../../core/applications/pre-application.types';

/**
 * The business dashboard's activity feed and pre-application banner, as pure
 * builders of the records the page loaded — no signals, no services, so each
 * rule here is checked without a TestBed.
 */

/** Stages that need the applicant's attention — shown instead of the coarser KS/DF status. */
const ATTENTION_STAGES = new Set(['DRAFT', 'MORE_INFO_REQUIRED', 'DECLINED']);

/** Copy for the dashboard pre-application banner, per in-flight review state. */
export interface PreAppNotice {
  status: PreApplicationReviewStatus;
  title: string;
  body: string;
  cta: string;
  /** Where the CTA goes — see `preAppLink`. */
  routerLink: unknown[];
  /** A second, quieter action — "Add documents" while under review. */
  secondaryCta?: string;
  secondaryLink?: unknown[];
}

/**
 * Where a pre-application CTA should land, by review state.
 *
 * 🚨 SUBMITTED goes to the read-only view (FLOW-223); the other two go to the
 * wizard. The split is the whole point: the wizard deliberately refuses to
 * hydrate a SUBMITTED record so the client can always start again, which meant
 * "View submission" opened a blank wizard. OPEN and DECLINED are genuinely
 * editable and their CTAs say so ("Continue", "Review and resubmit"), so they
 * must keep going to the wizard.
 *
 * PROCEEDED gets no link because it gets no banner and no activity row — its
 * KS/DF drafts stand in for it and appear in the feed on their own.
 */
export function preAppLink(
  entityId: string,
  status: PreApplicationReviewStatus,
  shortId: string,
): unknown[] {
  return status === 'SUBMITTED'
    ? ['/entity', entityId, 'applications', 'submission', shortId]
    : ['/entity', entityId, 'applications', 'new'];
}
/** Where "Add documents" lands for a SUBMITTED enquiry (add-only; PO ruling 2026-09-22). */
export function preAppAddDocumentsLink(entityId: string, shortId: string): unknown[] {
  return ['/entity', entityId, 'applications', 'submission', shortId, 'documents'];
}
/** Copy only — the CTA's destination comes from `preAppLink`, not from here. */
type PreAppNoticeCopy = Omit<
  PreAppNotice,
  'status' | 'routerLink' | 'secondaryCta' | 'secondaryLink'
>;

export const PRE_APP_NOTICES: Partial<Record<PreApplicationReviewStatus, PreAppNoticeCopy>> = {
  // Apply for funding starts a new application, so this is the way back to a draft.
  OPEN: {
    title: 'You have an application in progress',
    body: "Everything you've added is saved. Pick up where you left off.",
    cta: 'Continue',
  },
  SUBMITTED: {
    title: 'CapX is reviewing your details',
    body: "We're looking at what you shared and will be in touch with your funding options.",
    cta: 'View submission',
  },
  DECLINED: {
    title: "We couldn't proceed just yet",
    body: 'Nothing is final — you can update your details and submit again whenever things change.',
    cta: 'Review and resubmit',
  },
  // PROCEEDED (drafts show in the activity feed below) deliberately gets no banner.
};

/** One row in the dashboard's "recent applications" panel, merged from KS + DF. */
export interface ActivityItem {
  short_id: string;
  /** Router link for the row. KS/DF rows open the record; a pre-application row
   *  goes wherever `preAppLink` sends its review state. */
  routerLink: unknown[];
  productLabel: string;
  amount: string | null;
  status: string;
  isDraft: boolean;
  created_at: string;
  /** A funding enquiry that asked to be considered for Debtors Finance too (`df_intent`). */
  dfInterest?: boolean;
}

function activityStatus(stage: string, status: string): string {
  return ATTENTION_STAGES.has(stage) ? stage : status;
}

/** Merge KS applications + DF facilities into one newest-first activity feed. */
export function buildActivity(
  entityId: string,
  ks: KsApplication[],
  df: DfFacility[],
): ActivityItem[] {
  const ksItems: ActivityItem[] = ks.map((a) => ({
    short_id: a.short_id,
    routerLink: ['/entity', entityId, 'kickstart', a.short_id],
    productLabel: 'Kickstart Cash',
    amount: hasConfirmedAmount(a) ? a.amount_requested : null,
    status: activityStatus(a.application_stage, a.ks_status),
    isDraft: a.application_stage === 'DRAFT',
    created_at: a.created_at,
  }));
  const dfItems: ActivityItem[] = df.map((f) => ({
    short_id: f.short_id,
    routerLink: ['/entity', entityId, 'df', f.short_id],
    productLabel: 'Debtors Finance',
    amount: f.requested_limit,
    status: activityStatus(f.application_stage, f.df_status),
    isDraft: f.application_stage === 'DRAFT',
    created_at: f.created_at,
  }));
  return [...ksItems, ...dfItems].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
}

/**
 * Review states where a submitted pre-application is a real record the client
 * should see in "Recent applications" — it's been sent to CapX but hasn't yet
 * become KS/DF applications. OPEN (not yet submitted) is a draft-in-progress,
 * and PROCEEDED means the KS/DF drafts now stand in for it — both are omitted.
 */
const PRE_APP_ACTIVITY_STATES = new Set<PreApplicationReviewStatus>([
  'SUBMITTED',
  'MORE_INFO_REQUIRED',
  'DECLINED',
]);

/** The pre-application as an activity row, or null when it shouldn't be listed. */
export function preApplicationActivity(
  entityId: string,
  pa: PreApplication | null,
): ActivityItem | null {
  if (!pa || !PRE_APP_ACTIVITY_STATES.has(pa.review_status)) {
    return null;
  }
  return {
    short_id: pa.short_id,
    routerLink: preAppLink(entityId, pa.review_status, pa.short_id),
    productLabel: 'Funding enquiry',
    amount: null,
    status: pa.review_status,
    isDraft: false,
    created_at: pa.submitted_at ?? pa.created_at,
    dfInterest: pa.df_intent,
  };
}
